// opponent_may_swap_pile_ends. Pure helpers. No React, no Three.js.
//
// ── Codex (Riddle Sphinx, Minion) ──
//   "Genesis → Look at your topmost spell. You may put it on the bottom
//   of your spellbook, then an opponent may exchange your top and
//   bottommost spells. Draw a card."
//
// The middle clause hands a single yes/no decision to the OPPONENT: may
// they swap the controller's top and bottom spellbook cards. The decision
// belongs to the non-controller, but the mutation lands on the
// controller's OWN spellbook. And in multiplayer only the controller's
// client holds that pile's full card identities. So the decision and the
// mutation live on opposite clients and have to be reconciled, never
// decided independently on each side.
//
// Flow (controller C, opponent O):
//   1. C emits `requestPileEndSwap` to O with { pileId, requestId } after
//      the scry-to-bottom resolves.
//   2. O's client surfaces a yes/no Confirm and replies
//      `pileEndSwapResult` with { requestId, swap }.
//   3. C's handler (correlated by requestId) swaps the ends of the OWNED
//      pile on a `swap` reply and broadcasts the new order via pile:sync,
//      so both clients agree; then the deferred draw fires either way.
//
// Pile order convention (matches scry `from: 'top'`): index length-1 is
// the top, index 0 is the bottom.

/**
 * Swap the first and last cards of a pile's `cards` array in place.
 * No-op for a pile with fewer than two cards (top === bottom or empty).
 *
 * @param {Array} cards the pile's card array (mutated in place)
 * @returns {boolean} true iff a swap actually happened
 */
export function swapPileEnds(cards) {
  if (!Array.isArray(cards) || cards.length < 2) return false;
  const last = cards.length - 1;
  const tmp = cards[0];
  cards[0] = cards[last];
  cards[last] = tmp;
  return true;
}

/**
 * Controller side. Emit the swap request to the opponent and return the
 * request id (so the caller can correlate the reply + chain the draw).
 *
 * Host adapter:
 *   opponentSeat(controller): 'p1' | 'p2'
 *   newRequestId(): string
 *   emitSwapRequest(opponentSeat, pileId, requestId): void
 *
 * @param {string} controller 'p1' | 'p2'. The controller's seat
 * @param {string} pileId     the controller's spellbook pile id
 * @param {object} host
 * @returns {string} the request id
 */
export function requestOpponentSwap(controller, pileId, host) {
  const opponent = host.opponentSeat(controller);
  const requestId = host.newRequestId();
  host.emitSwapRequest(opponent, pileId, requestId);
  return requestId;
}
